'use client';

/* ============================================================================
 * CompareV2 - Instant Site vs <competitor> (/compare/<slug>/).
 * ============================================================================
 * Shared screen for the three comparison pages (Wix, Squarespace,
 * WordPress). Section order:
 *   1  Hero     - honest framing of the comparison
 *   2  Verdict  - the short version, two paragraphs
 *   3  Table    - side-by-side on the dimensions hotels care about
 *   4  Trade    - where each platform genuinely wins
 *   5  FAQ      - hotelier questions (FAQPage schema in the route)
 *   6  Closing  - demo CTA + links to the other comparisons
 * Copy lives in messages/<locale>.json under Pages.Compare: `common`
 * holds shared labels, `items.<slug>` the per-competitor copy.
 * ========================================================================= */

import React, {useState} from 'react';
import {useTranslations} from 'next-intl';
import {Link} from '@/i18n/navigation';
import {Reveal, RevealStagger, RevealLine} from '@/components/v2/Reveal';

const NS = 'Pages.Compare';
const TOTAL = 6;

export const COMPARE_SLUGS = ['wix', 'squarespace', 'wordpress'];

function Container({children, className = ''}) {
  return <div className={`container-page ${className}`}>{children}</div>;
}

function SectionAnchor({id, label}) {
  return (
    <Reveal className="mb-10 flex items-center gap-3 text-foreground-subtle">
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {String(id).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
      </span>
      <RevealLine className="block h-px flex-1 bg-border" delay={0.1} />
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">{label}</span>
    </Reveal>
  );
}

function CheckIcon({className = ''}) {
  return (
    <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M11.5 4L5.5 10L2.5 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SectionHero({slug}) {
  const t = useTranslations(NS);
  return (
    <section className="bg-hero-mesh relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
      <Container>
        <SectionAnchor id={1} label={t('common.anchors.hero')} />
        <Reveal eager>
          <p className="fig-label">{t(`items.${slug}.hero.eyebrow`)}</p>
        </Reveal>
        <Reveal delay={0.06} eager>
          <h1 className="headline-hero mt-5 max-w-[20ch] text-foreground">
            {t(`items.${slug}.hero.headline`)}
          </h1>
        </Reveal>
        <Reveal delay={0.12} eager>
          <p className="lead mt-6 max-w-[64ch]">{t(`items.${slug}.hero.lead`)}</p>
        </Reveal>
        <Reveal delay={0.18} eager>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link href="/request-demo/" className="cta-pill-brand">
              {t('common.hero.primaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
            <Link href="/features/" className="cta-text">
              {t('common.hero.secondaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function SectionVerdict({slug}) {
  const t = useTranslations(NS);
  const paragraphs = t.raw(`items.${slug}.verdict`);
  return (
    <section className="section-rhythm bg-background-subtle">
      <Container>
        <SectionAnchor id={2} label={t('common.anchors.verdict')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <h2 className="headline-section max-w-[14ch] text-foreground">
                {t('common.verdictLabel')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8 space-y-6">
            {paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <p className="text-[16px] leading-relaxed text-foreground-muted md:text-[17px]">
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function SectionTable({slug, name}) {
  const t = useTranslations(NS);
  const rows = t.raw(`items.${slug}.table`);
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={3} label={t('common.anchors.table')} />
        <Reveal>
          <div className="surface-elevated overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-5 font-mono text-[11px] font-medium tracking-[0.14em] uppercase text-foreground-subtle">
                    {t('common.tableHeadDimension')}
                  </th>
                  <th className="p-5 font-mono text-[11px] font-medium tracking-[0.14em] uppercase text-primary">
                    {t('common.tableHeadUs')}
                  </th>
                  <th className="p-5 font-mono text-[11px] font-medium tracking-[0.14em] uppercase text-foreground-subtle">
                    {name}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.dimension} className="border-b border-border-subtle last:border-b-0">
                    <td className="p-5 align-top text-[14px] font-medium tracking-tight text-foreground">
                      {row.dimension}
                    </td>
                    <td className="p-5 align-top text-[14px] leading-relaxed text-foreground">
                      <span className="flex items-start gap-2.5">
                        <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                        <span>{row.us}</span>
                      </span>
                    </td>
                    <td className="p-5 align-top text-[14px] leading-relaxed text-foreground-muted">
                      {row.them}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function SectionTradeoffs({slug, name}) {
  const t = useTranslations(NS);
  const them = t.raw(`items.${slug}.them`);
  const us = t.raw(`items.${slug}.us`);
  return (
    <section className="section-rhythm bg-background-subtle">
      <Container>
        <SectionAnchor id={4} label={t('common.anchors.us')} />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal>
            <article className="surface-elevated h-full p-7 md:p-8">
              <p className="fig-label">{t('common.themLabel', {name})}</p>
              <ul className="mt-6 space-y-4">
                {them.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[14.5px] leading-relaxed text-foreground-muted"
                  >
                    <span
                      aria-hidden
                      className="mt-[9px] size-1.5 flex-shrink-0 rounded-full bg-foreground-subtle"
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
          <Reveal delay={0.08}>
            <article className="surface-elevated h-full p-7 md:p-8">
              <p className="fig-label text-primary">{t('common.usLabel')}</p>
              <ul className="mt-6 space-y-4">
                {us.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[14.5px] leading-relaxed text-foreground"
                  >
                    <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function FaqItem({item, open, onToggle}) {
  return (
    <li className="border-b border-border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-6 py-6 text-left"
      >
        <span className="text-[16px] font-medium tracking-tight text-foreground md:text-lg">
          {item.q}
        </span>
        <span
          aria-hidden
          className={`mt-1 flex size-6 flex-shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-transform duration-200 ${
            open ? 'rotate-45' : ''
          }`}
        >
          <svg viewBox="0 0 12 12" className="size-3" fill="none">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="max-w-[70ch] pb-6 text-[14.5px] leading-relaxed text-foreground-muted">
            {item.a}
          </p>
        </div>
      </div>
    </li>
  );
}

function SectionFaq({slug}) {
  const t = useTranslations(NS);
  const items = t.raw(`items.${slug}.faq`);
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={5} label={t('common.anchors.faq')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <h2 className="headline-section max-w-[14ch] text-foreground">
                {t('common.faq.headline')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.08}>
              <ul className="border-t border-border">
                {items.map((item, i) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    open={openIndex === i}
                    onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                  />
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

function SectionClosing({slug}) {
  const t = useTranslations(NS);
  const others = COMPARE_SLUGS.filter((s) => s !== slug);
  return (
    <section className="section-rhythm-lg bg-background-subtle">
      <Container>
        <SectionAnchor id={6} label={t('common.anchors.closing')} />
        <Reveal>
          <h2 className="headline-manifesto max-w-[20ch] text-foreground">
            {t('common.closing.headline')}
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="lead mt-6 max-w-[52ch]">{t('common.closing.body')}</p>
        </Reveal>
        <Reveal delay={0.14}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link href="/request-demo/" className="cta-pill-brand">
              {t('common.closing.primaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
            <Link href="/templates/" className="cta-text">
              {t('common.closing.secondaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-16 border-t border-border pt-8">
            <p className="fig-label">{t('common.otherComparisons.headline')}</p>
            <p className="mt-3 max-w-[58ch] text-[14px] leading-relaxed text-foreground-muted">
              {t('common.otherComparisons.lead')}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {others.map((s) => (
                <Link key={s} href={`/compare/${s}/`} className="cta-text-brand text-[13.5px]">
                  {t(`items.${s}.hero.eyebrow`)} <span aria-hidden>&rarr;</span>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export default function CompareV2({slug}) {
  const t = useTranslations(NS);
  const name = t(`items.${slug}.name`);
  return (
    <>
      <SectionHero slug={slug} />
      <SectionVerdict slug={slug} />
      <SectionTable slug={slug} name={name} />
      <SectionTradeoffs slug={slug} name={name} />
      <SectionFaq slug={slug} />
      <SectionClosing slug={slug} />
    </>
  );
}
